import { ComponentRef, inject, InjectionToken, Injector, Service, Type } from '@angular/core';
import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Observable, Subject } from 'rxjs';

/**
 * Injection token a panel component reads its data through.
 *
 * Moved from the console's `PanelService` unchanged, so its ~160 panels keep
 * working; only the names gained the `Bz` prefix.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- each panel types its own data at the injection site
export const BZ_PANEL_DATA = new InjectionToken<any>('BZ_PANEL_DATA');

/**
 * Where the panel opens relative to the element that was clicked.
 *
 * `string`, not the CDK literal unions: the console's callers build this as an
 * object literal, which TypeScript widens to `string`, and the service has
 * always accepted that. Values are `start | center | end` horizontally and
 * `top | center | bottom` vertically.
 */
export interface BzPanelPosition {
  originX?: string;
  originY?: string;
  overlayX?: string;
  overlayY?: string;
  /** Accepted and ignored, as before — only the vertical offset is applied. */
  offsetX?: number;
  offsetY?: number;
}

export interface BzPanelConfiguration {
  position?: BzPanelPosition;
  /** Default true. */
  hasBackdrop?: boolean;
  height?: number | string;
  width?: number | string;
}

export interface BzPanelRef<T> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- whatever the panel's onClose output emits
  afterClose$: Observable<any>;
  instance?: T;
}

/**
 * Opens a component in a floating panel next to the element that was clicked.
 *
 * This is the console's `PanelService`, moved here as it was. The contract is
 * unchanged:
 *
 * - the anchor is `event.target`, so pass the click event;
 * - the panel component closes itself by emitting its `onClose` output, and
 *   whatever it emits is what `afterClose$` delivers;
 * - a click on the backdrop closes the panel and delivers `null`;
 * - the panel reads its data through `BZ_PANEL_DATA`;
 * - `afterClose$` completes without a value if the panel is removed by `close()`/`closeAll()`.
 */
@Service()
export class BzPanelService {
  private readonly overlay = inject(Overlay);
  private readonly injector = inject(Injector);

  private overlays: OverlayRef[] = [];

  open<T>(
    component: Type<T>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- a click event, or any object with a `target` element
    event: any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- each panel types its own data
    data?: any,
    configuration?: BzPanelConfiguration,
  ): BzPanelRef<T> | undefined {
    if (!event) return;

    const element = event.target as HTMLElement;

    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(element)
      .withPositions([
        {
          originX: (configuration?.position?.originX || 'center') as ConnectedPosition['originX'],
          originY: (configuration?.position?.originY || 'center') as ConnectedPosition['originY'],
          overlayX: (configuration?.position?.overlayX || 'center') as ConnectedPosition['overlayX'],
          overlayY: (configuration?.position?.overlayY || 'center') as ConnectedPosition['overlayY'],
          offsetY: configuration?.position?.offsetY !== undefined ? configuration.position.offsetY : 0,
        },
        {
          originX: 'center',
          originY: 'top',
          overlayX: 'center',
          overlayY: 'bottom',
          offsetY: -10,
        },
        {
          originX: 'center',
          originY: 'bottom',
          overlayX: 'center',
          overlayY: 'top',
          offsetY: 10,
        },
      ])
      .withPush(true)
      .withViewportMargin(20)
      .withFlexibleDimensions(false);

    const overlayRef = this.overlay.create({
      hasBackdrop: configuration?.hasBackdrop !== undefined ? configuration.hasBackdrop : true,
      backdropClass: 'cdk-overlay-backdrop',
      positionStrategy,
      height: configuration?.height,
      width: configuration?.width,
    });

    this.overlays.push(overlayRef);

    const injector = Injector.create({
      parent: this.injector,
      providers: [
        { provide: BZ_PANEL_DATA, useValue: data },
        { provide: OverlayRef, useValue: overlayRef },
      ],
    });
    const componentPortal = new ComponentPortal(component, null, injector);

    const componentRef: ComponentRef<T> = overlayRef.attach(componentPortal);

    // Automatically reposition when the content size changes (initial render, loading, etc.)
    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        if (overlayRef.hasAttached()) {
          overlayRef.updatePosition();
        }
      });
    });
    resizeObserver.observe(overlayRef.overlayElement);

    const instance = componentRef.instance;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- whatever the panel's onClose output emits
    const afterClose$ = new Subject<any>();

    // Closes the panel and hands its result over. `settled` exists because disposing the overlay
    // detaches it synchronously, before the result is emitted.
    let settled = false;
    const settle = (result: unknown): void => {
      settled = true;
      this.close(overlayRef);
      afterClose$.next(result);
      afterClose$.complete();
    };

    // Clean up observer when overlay is detached. Completing here is the one addition to the
    // console's version: a panel removed by close()/closeAll() used to leave afterClose$ open
    // forever, so a caller awaiting it never resumed.
    overlayRef.detachments().subscribe(() => {
      resizeObserver.disconnect();
      if (!settled) {
        afterClose$.complete();
      }
    });

    // Listen for custom close event
    const onClose = (instance as { onClose?: { subscribe(next: (result: unknown) => void): unknown } }).onClose;
    if (onClose) {
      onClose.subscribe((result: unknown) => settle(result));
    }

    // Close on backdrop click
    overlayRef.backdropClick().subscribe(() => settle(null));

    return { afterClose$: afterClose$.asObservable(), instance };
  }

  close(overlayRef: OverlayRef): void {
    const index = this.overlays.indexOf(overlayRef);
    if (index > -1) {
      overlayRef.dispose();
      this.overlays.splice(index, 1);
    }
  }

  closeAll(): void {
    this.overlays.forEach((ref) => ref.dispose());
    this.overlays = [];
  }
}
